import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { getAccountUISetting, saveAccountUISetting } from '@/shared/api/accountSettingsClient'
import { isWriteConflict } from '@/shared/api/errors'

import { sessionQueryOptions } from './session'

const UI_SETTINGS_QUERY_ROOT = 'account-ui-setting'
const SAVE_DEBOUNCE_MS = 300

/**
 * The stored setting as the cache holds it: the decoded value AND the revision
 * it came back at.
 *
 * The revision belongs HERE rather than in a ref, because it is a fact about
 * the setting rather than about one reader of it. Two components can hold the
 * same setting at once, and each writes the whole payload; a per-instance copy
 * would let the second one send a precondition the first one has already spent,
 * and its save would be refused for a conflict that never happened.
 */
export interface StoredSetting<TValue> {
  value: TValue
  revision: number
}

export interface PersistedUserSettingOptions<TValue> {
  scope: string
  scopeKey: string | null
  defaultValue: TValue
  enabled?: boolean
  encode: (value: TValue) => Record<string, unknown>
  decode: (payload: Record<string, unknown>) => TValue
}

type SettingQueryKey = readonly [string, string | null, string, string | null]

/**
 * The cache entry a setting is held in. Exported for a writer outside the hook
 * that must leave the hook's copy current — a choice saved before the reader
 * mounts would otherwise meet that reader's older copy first.
 */
export function settingQueryKey(
  account: string | null,
  scope: string,
  scopeKey: string | null,
): SettingQueryKey {
  return [UI_SETTINGS_QUERY_ROOT, account, scope, scopeKey]
}

export function usePersistedUserSetting<TValue>({
  scope,
  scopeKey,
  defaultValue,
  enabled = true,
  encode,
  decode,
}: PersistedUserSettingOptions<TValue>) {
  const queryClient = useQueryClient()
  /*
   * WHOSE SETTING THIS IS.
   *
   * Subscribe-only (`enabled: false`), for the reason `AppearanceProvider`
   * gives: this hook renders on the sign-in screen too, where asking `/auth/me`
   * is a guaranteed 401. It reads whatever the session query already holds and
   * never becomes the thing that asks.
   */
  const session = useQuery({ ...sessionQueryOptions(), enabled: false })
  const account = session.data?.identity.id ?? null
  const [value, setValue] = useState(defaultValue)
  const [saveTick, setSaveTick] = useState(0)
  const [reading, setReading] = useState({ scope, scopeKey, defaultValue })
  /** The setting whose stored answer `value` holds; `loaded` waits for it. */
  const [answered, setAnswered] = useState<string | null>(null)
  const latest = useRef(defaultValue)
  const dirty = useRef(false)
  const encodeRef = useRef(encode)
  const enabledRef = useRef(enabled)
  /** Whether a write of this setting is on the wire from THIS instance. */
  const inFlight = useRef(false)
  /**
   * The current flush, reachable from inside the previous one's answer.
   *
   * A ref rather than the callback itself, because the answer that releases a
   * held edit arrives arbitrarily later — after a re-render that rebuilt the
   * callback, and after an unmount that will schedule no further debounce.
   */
  const flushHeldEdit = useRef<() => void>(() => {})
  /**
   * THE IDENTITY THIS RENDER KNOWS ABOUT, for the save to check itself against.
   *
   * Signing out CLEARS the query cache, and an observer of a destroyed query
   * keeps handing back what it last saw until something re-renders it — this
   * provider lives above the router, so the something is usually the next edit.
   * The account read above is therefore RIGHT but sometimes LATE, and a save
   * decided from the closure that made it can be a save decided by the previous
   * session. The buffered edit is flushed a render later at the earliest — on
   * the debounce, on a changeover, on unmount — so this ref is what the account
   * is by THEN, and the flush below refuses to travel when the two disagree.
   */
  const knownAccount = useRef(account)
  knownAccount.current = account
  const queryKey = settingQueryKey(account, scope, scopeKey)

  /**
   * WHICH SETTING THE HELD VALUE BELONGS TO, RESET DURING RENDER RATHER THAN
   * AFTER IT. When a caller points this hook at a different setting, the value
   * it is still holding belongs to the previous one, and an effect cannot undo
   * that in time: the render that changed the scope has already returned the old
   * setting's value to the caller and been painted. A registry that switched
   * registries would show the previous one's stored filters for a frame, and a
   * panel that switched panels would open at the previous panel's remembered
   * width — someone else's preference, briefly, on a surface that never had one.
   *
   * This is React's own "adjust state when props change": the assignment
   * re-renders this component immediately, before its children run and before
   * anything is committed, so no caller ever sees the crossing.
   *
   * The refs are DELIBERATELY NOT touched here. `latest`/`dirty` carry an edit
   * that has not reached the server yet, and the save that flushes it runs in
   * this effect's cleanup — after this render. Clearing them now would drop the
   * clinician's last change to the setting they just navigated away from.
   *
   * THE ACCOUNT IS NOT ONE OF THESE CROSSINGS, and deliberately so. The scope
   * arrives as a prop, so the render that changes it is the render that knows;
   * the identity arrives from a cache this hook only observes, and after a
   * sign-out it can be learned in the very render that is carrying somebody's
   * first click on the login screen. Resetting the value there would eat that
   * click — the theme they just picked would flash back to the previous one —
   * so an account change drops the unsaved BUFFER (below) and leaves what is on
   * screen alone until the arriving account's own answer replaces it.
   */
  if (
    reading.scope !== scope ||
    reading.scopeKey !== scopeKey ||
    !Object.is(reading.defaultValue, defaultValue)
  ) {
    setReading({ scope, scopeKey, defaultValue })
    setValue(defaultValue)
  }

  useEffect(() => {
    encodeRef.current = encode
  }, [encode])

  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  // The rest of the changeover, which belongs after the commit: the pending
  // save above has flushed by now, so the edit buffer can be handed to the new
  // setting and the debounce timer stood down.
  useEffect(() => {
    latest.current = defaultValue
    dirty.current = false
    setSaveTick(0)
  }, [defaultValue, scope, scopeKey])

  /**
   * AN ACCOUNT CHANGE DROPS THE UNSAVED EDIT, and only that.
   *
   * This hook outlives the session — the provider holding the appearance is
   * mounted above the router — so a choice made on the sign-in screen is still
   * in this buffer when the next person signs in. Left there it would do two
   * things at once: hide the arriving account's own stored answer behind the
   * dirty guard below, and become the payload of the next save into their row.
   * A choice made signed out applies to the eye and is dropped at the next
   * sign-in, which is the whole of what it was ever entitled to.
   *
   * `latest` is NOT reset to the default beside it, because the displayed value
   * is not reset either: the two must keep saying the same thing, and the
   * arriving account's answer replaces both together.
   */
  useEffect(() => {
    dirty.current = false
    setSaveTick(0)
  }, [account])

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }): Promise<StoredSetting<TValue>> => {
      const setting = await getAccountUISetting(scope, scopeKey, signal)
      // A focus read can finish before an outstanding save reaches the server,
      // or arrive after its acknowledgement with an older revision. Keep the
      // pending choice and its precondition together; rebasing either half here
      // would undo the choice or permit it to overwrite another tab's edit.
      const current = queryClient.getQueryData<StoredSetting<TValue>>(queryKey)
      if (current && (dirty.current || inFlight.current || current.revision > setting.revision)) {
        return current
      }
      return { value: decode(setting.payload), revision: setting.revision }
    },
    enabled,
    retry: false,
    /*
     * THE ONE QUERY THAT REFETCHES ON FOCUS, against the global `false` in
     * `main.tsx`.
     *
     * Everything else in the product is read for the screen in front of
     * someone; this is read to decide what a WRITE will replace. A tab left
     * open on a second monitor holds its copy for as long as it is open — the
     * quick-access provider lives for the life of the shell — so without this
     * it composes a full-payload replacement from a copy that may be hours old,
     * and everything the other tab saved in between is gone. Coming back to the
     * tab is the moment before the next edit, so it is the moment to rebase.
     * The precondition below still refuses a write that raced anyway; this is
     * what keeps that refusal rare instead of ordinary.
     */
    refetchOnWindowFocus: 'always',
  })

  /*
   * The answer reaches `value` one render after the query has it, so `loaded`
   * is raised here beside it rather than read from the query: a caller that
   * decides on the stored answer must never decide on the default instead.
   * An edit made before the answer arrived is the value, and loaded too.
   */
  const answerKey = JSON.stringify(queryKey)
  useEffect(() => {
    if (!query.isSuccess) {
      return
    }
    setAnswered(answerKey)
    if (dirty.current) {
      return
    }
    latest.current = query.data.value
    setValue(query.data.value)
  }, [query.data, query.isSuccess, answerKey])

  /**
   * ONE SETTING, ONE ANSWER, ON EVERY SURFACE READING IT RIGHT NOW.
   *
   * Two components can hold the same setting at the same time — a screen with
   * two registries on it holds the page length twice — and each holds its own
   * copy of the value. So the write is published to the QUERY the others read
   * from, not just to this instance's state: the cache entry is keyed by
   * account, scope and key, so every mounted reader of this setting re-renders
   * with the new answer and the reader is not left looking at two tables that
   * disagree about a preference they just set once. A sibling holding an
   * unsaved edit of its own keeps it — `dirty` guards its sync effect — and the
   * debounced save below still carries the value to the account exactly once.
   *
   * The revision beside it is carried through UNCHANGED: this is a local edit,
   * not an answer from the server, so it changes what the payload says and not
   * which stored setting the payload was built on.
   *
   * The refs are updated HERE rather than inside a state updater. React may
   * call an updater more than once for one write, and `latest`/`dirty` decide
   * what reaches the server; a resolved value derived from the ref keeps a
   * chained edit — the bookmark editor writes several in a row — reading the
   * value the one before it produced.
   */
  const setPersistedValue = useCallback(
    (next: TValue | ((current: TValue) => TValue)) => {
      const resolved =
        typeof next === 'function' ? (next as (value: TValue) => TValue)(latest.current) : next
      latest.current = resolved
      dirty.current = true
      setValue(resolved)
      setSaveTick((tick) => tick + 1)
      queryClient.setQueryData<StoredSetting<TValue>>(
        settingQueryKey(account, scope, scopeKey),
        (current) => ({ value: resolved, revision: current?.revision ?? 0 }),
      )
    },
    [account, queryClient, scope, scopeKey],
  )

  const saveLatest = useCallback(() => {
    // THE ACCOUNT THIS PAYLOAD WAS COMPOSED UNDER IS THE ONLY ONE IT MAY REACH.
    // Every flush runs a render or more after the edit — the debounce, the
    // changeover cleanup, the unmount — and by then the identity may have moved
    // on and this callback still be the one the previous session left behind.
    // A whole-payload replacement sent into the row of whoever arrived next is
    // the lost update the revision cannot catch, because it would be perfectly
    // well-formed.
    if (!enabledRef.current || !dirty.current || knownAccount.current !== account) {
      return
    }
    // HELD, NOT SENT, while a write of this setting is already on the wire.
    // `dirty` stays set and the answer to that write is what releases this one,
    // composed on the revision the answer produced. Sending both on the
    // revision they were both composed from is the two-tab race with both tabs
    // in one head: the first is accepted, the second refused, and the recovery
    // pulls the screen back to the choice this person had just moved on from.
    // That is worse than the lost update the precondition prevents — both
    // choices were made deliberately, seconds apart, by the same hand.
    if (inFlight.current) {
      return
    }
    const key = settingQueryKey(account, scope, scopeKey)
    const payload = encodeRef.current(latest.current)
    // Read at FLUSH time, not at edit time: a sibling reader of the same
    // setting may have saved since this edit was typed, and the revision that
    // has to travel is the one the shared cache holds now.
    const expectedRevision = queryClient.getQueryData<StoredSetting<TValue>>(key)?.revision ?? 0
    dirty.current = false
    inFlight.current = true
    void saveAccountUISetting(scope, scopeKey, payload, expectedRevision)
      .then((saved) => {
        inFlight.current = false
        // Only the revision. The payload just written is already in the cache,
        // and a sibling may have edited it again while this request was in
        // flight; echoing the server's copy back over that would undo an edit
        // nobody was told about.
        queryClient.setQueryData<StoredSetting<TValue>>(key, (current) =>
          current ? { ...current, revision: saved.revision } : current,
        )
        // An edit made while this was on the wire was held above. Released
        // HERE rather than left to the next debounce, because there may not be
        // a next one: the hold can outlive the component that made the edit,
        // and this answer is the last moment at which anything still knows the
        // edit exists.
        if (dirty.current) {
          flushHeldEdit.current()
        }
      })
      .catch((error: unknown) => {
        inFlight.current = false
        if (isWriteConflict(error)) {
          // REFUSED, NOT FAILED. Someone else's save is what is stored, and
          // this payload was composed without it. Re-reading is the only
          // honest recovery — retrying would be the lost update the
          // precondition just prevented, one round trip later.
          void queryClient.invalidateQueries({ queryKey: key })
          return
        }
        dirty.current = true
      })
  }, [account, queryClient, scope, scopeKey])
  flushHeldEdit.current = saveLatest

  useEffect(() => {
    if (saveTick === 0) {
      return undefined
    }
    const timer = setTimeout(saveLatest, SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [saveLatest, saveTick])

  useEffect(() => {
    // A document reload does not ask React to unmount its root, so component
    // cleanup alone is not a reload boundary. Start the final bounded,
    // keepalive account-setting request while the page still has a lifecycle;
    // ordinary route unmounts keep the same immediate flush below.
    const flushForPageExit = () => saveLatest()
    window.addEventListener('pagehide', flushForPageExit)
    return () => {
      window.removeEventListener('pagehide', flushForPageExit)
      saveLatest()
    }
  }, [saveLatest])

  return {
    value,
    setValue: setPersistedValue,
    loaded: query.isSuccess && answered === answerKey,
    unavailable: query.isError,
  }
}
